import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import ErrorPopup from "../../../../components/element/ErrorPopup";
import Loader from "../../../../components/element/Loader";
import NoDataPlaceholder from "../../../../components/element/NoDataPlaceholder";
import useAirlineAdminApi from "../../../../services/api/useAirlineAdminApi";
import { StorageContext } from "../../../../services/StorageProvider";
import { DATE_FORMAT, PAGINATION_PAGE_SIZE } from "../../../../utils/constants";
import {
  getAirportCode,
  getAirportData,
  getFlightNumber,
} from "../../../../utils/utils";
import "../css/AviationStackTable.css";
import dayjs from "dayjs";

const AviationStackTable = ({
  isLoading,
  error,
  aviationStackData,
  isSuccess,
  airlineName,
}) => {
  const { fetchAircraftDetails } = useAirlineAdminApi();
  const { airportData } = useContext(StorageContext);

  const { data: aircraftData } = useQuery({
    queryKey: ["get-aircraft-data", aviationStackData?.data?.aircraftTypeId],
    queryFn: () =>
      fetchAircraftDetails(aviationStackData?.data?.aircraftTypeId),
    enabled: !!aviationStackData?.data?.aircraftTypeId,
  });

  const parseFlightData = (flightDataString) => {
    try {
      if (!flightDataString) return null;
      const flightData = JSON.parse(flightDataString);
      return {
        departureAirport: getAirportCode(flightData.departure),
        arrivalAirport: getAirportCode(flightData.arrival),
        airline:
          flightData.airline?.name === "empty"
            ? "-"
            : flightData.airline?.name || "-",
        flightNumber: flightData.flight?.number || "-",
        departureTime: flightData.departure?.scheduled,
        arrivalTime: flightData.arrival?.scheduled,
        aircraft: flightData.aircraft || "-",
        status: "failure",
        flightDate: flightData.flight_date || "-",
      };
    } catch (e) {
      console.error("Error parsing flight data:", e);
      return null;
    }
  };

  const getAircraftName = (aircraftTypeId) => {
    if (!aircraftTypeId) return "-";
    if (!aircraftData?.data) return "-";
    const aircraft = aircraftData?.data?.find(
      (aircraft) => aircraft.id === aircraftTypeId
    );
    return aircraft?.icaoCode || "-";
  };

  const getAirportName = (airportId) => {
    const airport = getAirportData(airportData, airportId);
    return getAirportCode(airport);
  };

  if (error) {
    return <ErrorPopup children={error.message}></ErrorPopup>;
  }

  if (isLoading) {
    return <Loader />;
  }

  if (aviationStackData && aviationStackData.data.content.length === 0) {
    return <NoDataPlaceholder />;
  }

  const pageSize = PAGINATION_PAGE_SIZE;
  const currentPage = aviationStackData?.data?.number || 0;
  const startIndex = currentPage * pageSize;

  return (
    <div className="aviation-stack-table-parent-container">
      <div className="aviation-stack-table-container">
        <table className="aviation-stack-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Source Airport</th>
              <th>Destination Airport</th>
              <th>Airline</th>
              <th>Flight Number</th>
              <th>Flight Date</th>
              <th>Aircraft Type</th>
              {!isSuccess && (
                <>
                  <th>Status</th>
                  <th>Reason</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {aviationStackData?.data?.content?.map((item, index) => {
              const flightData = !isSuccess
                ? parseFlightData(item?.flightData)
                : null;

              return (
                <tr key={index}>
                  <td>{startIndex + index + 1}</td>
                  <td>
                    {isSuccess
                      ? getAirportName(item?.arrivalAirportId)
                      : flightData?.departureAirport}
                  </td>
                  <td>
                    {isSuccess
                      ? getAirportName(item?.departureAirportId)
                      : flightData?.arrivalAirport}
                  </td>
                  <td>{isSuccess ? airlineName : flightData?.airline}</td>
                  <td>
                    {isSuccess
                      ? getFlightNumber(item)
                      : getFlightNumber(flightData)}
                  </td>
                  <td>
                    {isSuccess
                      ? dayjs(item?.flightDate).format(DATE_FORMAT)
                      : dayjs(flightData?.flightDate).format(DATE_FORMAT)}
                  </td>
                  <td>
                    {isSuccess
                      ? getAircraftName(item?.aircraftTypeId)
                      : flightData?.aircraft}
                  </td>
                  {!isSuccess && (
                    <>
                      <td>{flightData?.status || item?.status}</td>
                      <td>{item?.failureReason}</td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AviationStackTable;
